import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import swimService from '../api/services/swimService';
import { SwimLeague } from '../types/swim';
import { useIsMobile } from '../hooks/useIsMobile';
import { useSnackbar } from '../components/common/SnackbarProvider';

type DialogMode = 'create' | 'edit' | null;

interface FormState {
  name: string;
  start_date: string;
  end_date: string;
  age_up_date: string;
  notes: string;
}

const emptyForm: FormState = { name: '', start_date: '', end_date: '', age_up_date: '', notes: '' };

function fmt(iso: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

const SwimLeaguesAdminPage: React.FC = () => {
  const isMobile = useIsMobile();
  const snackbar = useSnackbar();
  const [leagues, setLeagues] = useState<SwimLeague[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dialogMode, setDialogMode] = useState<DialogMode>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);

  useEffect(() => { void load(); }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setLeagues(await swimService.getLeagues());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load leagues');
    } finally {
      setLoading(false);
    }
  }

  function openCreate() {
    setForm(emptyForm);
    setEditingId(null);
    setDialogMode('create');
  }
  function openEdit(lg: SwimLeague) {
    setForm({
      name: lg.name,
      start_date: lg.start_date ?? '',
      end_date: lg.end_date ?? '',
      age_up_date: lg.age_up_date ?? '',
      notes: lg.notes ?? '',
    });
    setEditingId(lg.id);
    setDialogMode('edit');
  }

  const valid = Boolean(
    form.name.trim() && form.start_date && form.end_date && form.end_date >= form.start_date,
  );

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await swimService.upsertLeague({
        id: editingId ?? undefined,
        name: form.name.trim(),
        start_date: form.start_date,
        end_date: form.end_date,
        age_up_date: form.age_up_date || null,
        notes: form.notes.trim() || null,
      });
      setDialogMode(null);
      snackbar.success('League saved');
      await load();
    } catch (e: unknown) {
      type ErrLike = { response?: { data?: { error?: string } }; message?: string };
      const err = e as ErrLike;
      setError(err?.response?.data?.error || err?.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  async function remove(lg: SwimLeague) {
    if (!window.confirm(`Delete "${lg.name}"? Meets stay but lose their league tie (age groups fall back to current age).`)) return;
    setBusy(true);
    try {
      await swimService.deleteLeague(lg.id);
      snackbar.success('League deleted');
      await load();
    } catch (e: unknown) {
      type ErrLike = { response?: { data?: { error?: string } }; message?: string };
      const err = e as ErrLike;
      snackbar.error(err?.response?.data?.error || err?.message || 'Delete failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        spacing={{ xs: 1.5, sm: 0 }}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h4" component="h1">Swim Leagues</Typography>
          <Typography variant="body2" color="text.secondary">
            Seasons/series with a date range. Meets tie to a league; a swimmer's age group is locked to
            their age on the league's age-up date.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>New League</Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : leagues.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No leagues yet. Create one to group your meets into a season.</Typography>
        </Paper>
      ) : (
        <Stack spacing={1.5}>
          {leagues.map((lg) => (
            <Paper key={lg.id} variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                <EmojiEventsIcon sx={{ color: 'warning.main', mt: 0.5 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{lg.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {fmt(lg.start_date)} – {fmt(lg.end_date)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Age-up date: {fmt(lg.age_up_date || lg.start_date)}
                    {!lg.age_up_date && ' (defaults to start)'}
                  </Typography>
                  {lg.notes && <Typography variant="body2" sx={{ mt: 0.5 }}>{lg.notes}</Typography>}
                </Box>
                <IconButton onClick={() => openEdit(lg)} disabled={busy} title="Edit"><EditIcon fontSize="small" /></IconButton>
                <IconButton onClick={() => void remove(lg)} disabled={busy} color="error" title="Delete"><DeleteIcon fontSize="small" /></IconButton>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      <Dialog open={dialogMode !== null} onClose={() => setDialogMode(null)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>{dialogMode === 'create' ? 'New League' : 'Edit League'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              fullWidth
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Central Valley Recreational Swim League 2026"
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Start date"
                type="date"
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              />
              <TextField
                label="End date"
                type="date"
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              />
            </Stack>
            <TextField
              label="Age-up date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.age_up_date}
              onChange={(e) => setForm({ ...form, age_up_date: e.target.value })}
              helperText="Swimmers' age groups lock to their age on this date. Leave blank to use the start date."
            />
            <TextField
              label="Notes"
              fullWidth
              multiline
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogMode(null)} disabled={busy}>Cancel</Button>
          <Button onClick={() => void save()} variant="contained" disabled={busy || !valid}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SwimLeaguesAdminPage;
